import { memo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import NumberFormat from 'react-number-format';

// material
import { alpha, useTheme, styled } from '@mui/material/styles';
import { Box, Grid, Card, Typography, Stack, Skeleton } from '@mui/material';
// utils
import Chart, { useChart } from '../../../../components/chart';
import { fNumber, fPercent, fShortenNumber } from '../../../../utils/formatNumber';
// import { useDispatch, useSelector } from '../../../../redux/store';

import Iconify from '../../../../components/Iconify';
import { isNilOrBlank } from '../../../../utils/core';
import useAuth from '../../../../hooks/useAuth';
// ----------------------------------------------------------------------

const IconWrapperStyle = styled('div')(({ theme }) => ({
  width: 24,
  height: 24,
  display: 'flex',
  borderRadius: '50%',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.success.main,
  backgroundColor: alpha(theme.palette.success.main, 0.16)
}));

// ----------------------------------------------------------------------

//
Widget.propTypes = {
  params: PropTypes.object,
  editItem: PropTypes.func
};

function Widget({ params, editItem }) {
  const theme = useTheme();
  const { ability } = useAuth();
  const [miniChatData, setMiniChatData] = useState(undefined);
  const [percent, setPercent] = useState(0);

  const [chartOptions, setChartOptions] = useState(
    useChart({
      colors: [theme.palette.primary.main],
      // Stroke
      stroke: {
        show: true,
        width: ['widget-bar', 'N/A', 'N/ A'].includes(params.chart_type) ? 0.2 : 2.2,
        curve: 'straight',
        lineCap: 'round'
        // colors: ['transparent']
      },
      fill: {
        type: 'gradient',
        gradient: {
          colorStops: [
            { offset: 0, color: theme.palette.primary.main, opacity: 1 },
            { offset: 100, color: theme.palette.primary.light, opacity: 1 }
          ]
        }
      },
      chart: {
        toolbar: { show: false },
        animations: {
          enabled: true
        },
        sparkline: {
          enabled: true
        }
      },
      plotOptions: { bar: { columnWidth: '68%', borderRadius: 2 } },
      tooltip: {
        x: { show: true },
        y: {
          formatter: (seriesName) => fNumber(seriesName),
          title: {
            formatter: () => ''
          }
        },
        marker: { show: true }
      }
    })
  );

  useEffect(() => {
    if (!isNilOrBlank(params.data) && !isNilOrBlank(params.data.values) && Array.isArray(params.data.values)) {
      const values = params.data.values.map((v) => v.value);
      const { length } = values;
      setChartOptions({ ...chartOptions, labels: params.data.values.map((v) => v.period_label) });
      setMiniChatData([{ data: values }]);
      setPercent(values[length - 1] === 0 ? null : (values[length - 1] - values[length - 2]) / values[length - 1]);
    }
  }, [params.data]); // eslint-disable-line

  return (
    <>
      {params.isLoading === true && (
        <Card sx={{ display: 'flex', px: 3, py: 2, mt: 1 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Skeleton animation="wave" height={20} width="100%" />
            <Skeleton animation="wave" height={90} width="100%" />
          </Box>
        </Card>
      )}
      {!isNilOrBlank(params.data) &&
        !isNilOrBlank(params.data.values) &&
        !isNilOrBlank(miniChatData) &&
        miniChatData.length > 0 &&
        params.isLoading === false && (
          <Card
            sx={{ display: 'flex', px: 3, py: 2, mt: 1 }}
            onDoubleClick={() => {
              if (editItem !== undefined) {
                if (ability.can('create_item', 'Dashboard') && ability.can('update_item', 'Dashboard')) {
                  editItem(params);
                }
              }
            }}
          >
            <Box sx={{ flexGrow: 1 }}>
              <Stack direction="column" justifyContent="space-between" alignItems="flex-start" spacing={0}>
                {params.title !== undefined && (
                  <Typography variant="subtitle2" sx={{ width: '100%' }}>
                    {params.title}
                  </Typography>
                )}

                <Grid container direction="row" justifyContent="space-between" alignItems="center">
                  <Grid item xs={4}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1, mb: 1 }}>
                      <IconWrapperStyle
                        sx={{
                          ...(percent < 0 && {
                            color: 'error.main',
                            bgcolor: alpha(theme.palette.error.main, 0.16)
                          })
                        }}
                      >
                        <Iconify
                          icon={
                            percent >= 0 || Number.isNaN(percent) ? 'eva:trending-up-fill' : 'eva:trending-down-fill'
                          }
                          width={16}
                          height={16}
                        />
                      </IconWrapperStyle>
                      <Typography component="span" variant="caption" color="textSecondary">
                        {percent > 0 && '+'}
                        {!Number.isNaN(percent) && fPercent(percent)}
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid item xs="auto" sx={{ alignItems: 'right' }}>
                    <Chart
                      type={
                        params.chart_type === 'N/A' || isNilOrBlank(params.chart_type)
                          ? 'bar'
                          : params.chart_type.replace('widget-', '')
                      }
                      series={miniChatData}
                      options={chartOptions}
                      width={100}
                      height={50}
                    />
                  </Grid>
                  {/* <pre>{JSON.stringify(miniChatData, null, 2)}</pre> */}
                </Grid>

                <Typography variant="h3">
                  <NumberFormat
                    value={params.data.current_value}
                    displayType="text"
                    thousandSeparator
                    renderText={(value) => (
                      <>
                        {params.prefix !== undefined && (
                          <Typography color="primary" variant="body2" component="span" noWrap>
                            {params.prefix}
                          </Typography>
                        )}
                        {fShortenNumber(value)}
                        {params.suffix !== undefined && (
                          <Typography color="primary" variant="body2" component="span" noWrap>
                            {params.suffix}
                          </Typography>
                        )}
                      </>
                    )}
                  />
                </Typography>
              </Stack>
            </Box>
          </Card>
        )}
    </>
  );
}

export default memo(Widget);
